import type { ReactNode } from 'react'
import { useAutosave } from '@/core/hooks/useAutosave'
import { AutosaveStatus } from '@/core/components/form/AutosaveBoundary'
import { ModalHead, ScrollBar } from 'admination-react'
import { useEffect, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'
import { me } from '@/core/data/session'
import {
  listMassnahmeModuln,
  renameModul,
  updateMassnahmeModulUe,
  otherModulesUeSum,
  maxAcceptedModulUe,
  getModul,
  listModulUsageCounts,
  type MassnahmeModulRow,
} from '../../data/modul'
import { getMassnahmeUeBudget, type MassnahmeUeBudget } from '@/core/data/massnahmen'
import { TextField } from '@/core/components/form/TextField'

import { isUniqueViolation } from '@/core/lib/dbError'

export function ModulDetails({
  massnahmeId,
  modulId,
  actions,
}: {
  massnahmeId: string | null
  modulId: string | null
  actions?: ReactNode
}) {
  const { dispatch, state, reloadMassnahmen } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const [isOwner, setIsOwner] = useState(false)

  const [moduln, setModuln] = useState<MassnahmeModulRow[]>([])
  const [budget, setBudget] = useState<MassnahmeUeBudget | null>(null)

  const [catalogName, setCatalogName] = useState<string | null>(null)
  const [usageCount, setUsageCount] = useState(0)

  useEffect(() => {
    me()
      .then((m) => setIsOwner(m.isOwner))
      .catch((e) => console.error('me() failed:', e))
  }, [])

  useEffect(() => {
    if (!massnahmeId) return
    listMassnahmeModuln(massnahmeId)
      .then(setModuln)
      .catch((e) => console.error('Module laden fehlgeschlagen:', e))
  }, [massnahmeId, state.massnahmenVersion])

  useEffect(() => {
    if (!massnahmeId) return
    getMassnahmeUeBudget(massnahmeId)
      .then(setBudget)
      .catch((e) => console.error('UE-Budget laden fehlgeschlagen:', e))
  }, [massnahmeId])

  useEffect(() => {
    if (massnahmeId || !modulId) return
    getModul(modulId)
      .then((row) => setCatalogName(row?.name ?? null))
      .catch((e) => console.error('Modul laden fehlgeschlagen:', e))
  }, [massnahmeId, modulId, state.massnahmenVersion])

  useEffect(() => {
    if (massnahmeId || !modulId) return
    listModulUsageCounts()
      .then((counts) => setUsageCount(counts.get(modulId) ?? 0))
      .catch((e) => console.error('Verwendung laden fehlgeschlagen:', e))
  }, [massnahmeId, modulId, state.massnahmenVersion])

  const row = moduln.find((m) => m.modulId === modulId) ?? null
  const displayName = massnahmeId ? (row?.name ?? null) : catalogName

  const [name, setName] = useState('')
  const [ue, setUe] = useState('')
  const [initializedKey, setInitializedKey] = useState<string | null>(null)

  useEffect(() => {
    if (displayName == null) return
    const key = `${massnahmeId ?? ''}:${modulId ?? ''}`
    if (initializedKey !== key) {
      setInitializedKey(key)
      const v = { name: displayName, ue: row?.ueAnzahl != null ? String(row.ueAnzahl) : '' }
      setName(v.name)
      setUe(v.ue)
    }
  }, [displayName, row, massnahmeId, modulId, initializedKey])

  const close = () => dispatch({ type: 'CLOSE' })

  const commitName = (value: string) => {
    if (!modulId || !isOwner) return
    const trimmed = value.trim()
    if (!trimmed) throw new Error('Bitte geben Sie einen Modulnamen ein.')
    return renameModul(modulId, trimmed)
      .then(() => {
        reloadMassnahmen()
      })
      .catch((e) => {
        console.error('Modul umbenennen fehlgeschlagen:', e)
        throw isUniqueViolation(e) ? new Error('Dieses Modul gibt es in diesem Katalog schon.') : e
      })
  }

  const commitUe = (value: string) => {
    if (!row || !massnahmeId || !isOwner) return
    const trimmed = value.trim()
    const parsed = trimmed === '' ? null : Number(trimmed)
    if (parsed != null && (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0)) throw new Error('Bitte geben Sie eine gültige Anzahl UE ein.')

    if (parsed != null && budget?.ueAnzahl != null) {
      const otherSum = otherModulesUeSum(moduln, row.modulId)
      const maxAccepted = maxAcceptedModulUe(budget.ueAnzahl, otherSum)
      if (parsed > maxAccepted) {
        throw new Error(
          `UE-Budget überschritten: Budget ${budget.ueAnzahl} UE, bereits ${otherSum} UE für andere Module vergeben, maximal ${maxAccepted} UE für dieses Modul möglich.`,
        )
      }
    }

    return updateMassnahmeModulUe(massnahmeId, row.modulId, parsed)
      .then(() => {
        reloadMassnahmen()
      })
      .catch((e) => {
        console.error('UE speichern fehlgeschlagen:', e)
        throw e
      })
  }

  const relationKey = JSON.stringify([massnahmeId, modulId])
  const ready = displayName !== null && isOwner && initializedKey === `${massnahmeId ?? ''}:${modulId ?? ''}`
  useAutosave(name, ready, commitName, 250, { form: 'modul-name', record: modulId ?? undefined, label: displayName ?? 'Modul', recover: setName })
  useAutosave(ue, ready && !!massnahmeId && !!row, commitUe, 250, { form: 'modul-ue', record: relationKey, label: `${displayName ?? 'Modul'} (${budget?.label ?? 'Maßnahme'})`, recover: setUe })

  const onEnterBlur = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') (e.target as HTMLElement).blur()
  }

  return (
    <>
      <ModalHead titleIcon="i-module" title={displayName ?? 'Modul'} actions={<><AutosaveStatus />{actions}</>} onClose={close} closeLabel="Schließen" />
      <main className="form" data-scroll-host ref={scrollerRef}>
        <div className="section">
          <div className="section-body">
            <div onKeyDown={onEnterBlur}>
              <TextField
                label="Name"
                value={name}
                onChange={setName}
                readOnly={!ready}
                required
              />
            </div>
            {massnahmeId ? (
              <div onKeyDown={onEnterBlur}>
                <TextField
                  label="UE (diese Maßnahme)"
                  value={ue}
                  onChange={setUe}
                  readOnly={!ready}
                  inputMode="numeric"
                  filter={/[0-9]/}
                />
              </div>
            ) : (
              <TextField
                label="Verwendung"
                value={usageCount === 0 ? 'Nicht verwendet' : `In ${usageCount} Maßnahme${usageCount === 1 ? '' : 'n'} verwendet`}
                onChange={() => {}}
                readOnly
              />
            )}
          </div>
        </div>
      </main>
      <ScrollBar scrollerRef={scrollerRef} />
    </>
  )
}
